import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Container,
  IconButton,
  AppBar,
  Toolbar,
  Divider,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Logout as LogoutIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  PriorityHigh as HighPriorityIcon,
  RemoveCircle as MediumPriorityIcon,
  LowPriority as LowPriorityIcon,
  CheckCircle as ResolvedIcon,
  Cancel as ClosedIcon,
  PlayArrow as InProgressIcon,
  RadioButtonUnchecked as OpenIcon,
  Category as CategoryIcon,
  Group as GroupIcon,
  AccessTime as AccessTimeIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  Note as NoteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AttachFile as AttachFileIcon,
  Link as LinkIcon,
  Assignment as AssignmentIcon,
  VideoCall as VideoCallIcon,
  Image as ImageIcon,
  BugReport as BugReportIcon,
  Summarize as SummarizeIcon,
  Chat as ChatIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { ticketsService, type Ticket } from '../services/ticketsService';
import { usersService, type OrgUser } from '../services/usersService';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeModeContext';

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    assigned_team: '',
  });
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
  const [activeTab] = useState(0);
  const [history, setHistory] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [comments, setComments] = useState<any[]>([]);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [activityType, setActivityType] = useState<'comment' | 'attachment'>('comment');
  const [newActivity, setNewActivity] = useState({
    content: '',
    visibility: 'internal' as 'internal' | 'public' | 'private',
    attachmentType: 'file' as 'file' | 'link' | 'meeting' | 'screenshot' | 'log',
    title: '',
    description: '',
    url: '',
    transcription: '',
    file: null as File | null,
  });
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [showAISummaryDialog, setShowAISummaryDialog] = useState(false);
  const [aiSummary, setAiSummary] = useState<any>(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [editableSummary, setEditableSummary] = useState('');

  useEffect(() => {
    if (showActivityDialog && activityType === 'comment') {
      console.log('[CommentDialog] opening, attempting focus');
      requestAnimationFrame(() => {
        const active = document.activeElement as HTMLElement | null;
        console.log('[CommentDialog] before focus, activeElement:', active?.tagName, active?.className);
        commentInputRef.current?.focus();
        const after = document.activeElement as HTMLElement | null;
        console.log('[CommentDialog] after focus, activeElement:', after?.tagName, after?.className, 'refExists:', !!commentInputRef.current);
      });
    }
  }, [showActivityDialog, activityType]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [artifacts, setArtifacts] = useState<any[]>([]);

  useEffect(() => {
    if (ticketId) {
      fetchTicket();
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);


  const fetchTicket = async () => {
    if (!ticketId) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await ticketsService.getTicket(ticketId);
      setTicket(data);
      setEditData({
        status: data.status,
        priority: data.priority,
        assigned_to: data.assigned_to || '',
        assigned_team: data.metadata?.assigned_team || '',
      });
    } catch (e: any) {
      setError(e.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const performTicketUpdate = async () => {
    if (!ticket || !ticketId) return;

    try {
      await ticketsService.updateTicket(ticketId, {
        status: editData.status as any,
        priority: editData.priority as any,
        assigned_to: editData.assigned_to || undefined,
        metadata: {
          ...ticket?.metadata,
          assigned_team: editData.assigned_team || undefined,
          ...(aiSummary ? { ai_summary: { text: editableSummary, raw: aiSummary, updated_at: new Date().toISOString() } } : {}),
        },
      });
      await fetchTicket();
      setEditing(false);
      setShowAISummaryDialog(false);
      setAiSummary(null);
      setEditableSummary('');
    } catch (e: any) {
      setError(e.message || 'Failed to update ticket');
    }
  };

  // Load users for assignee selector
  useEffect(() => {
    (async () => {
      try {
        setUsersLoading(true);
        const u = await usersService.listUsers();
        setUsers(u);
      } catch (e) {
        console.error('Failed to load users', e);
      } finally {
        setUsersLoading(false);
      }
    })();
  }, []);

  const fetchHistory = async () => {
    if (!ticketId) return;
    
    try {
      setLoadingHistory(true);
      
      // Service layer now handles 405 errors gracefully and returns empty arrays
      const [historyData, notesData, artifactsData, commentsData] = await Promise.all([
        ticketsService.getTicketHistory(ticketId),
        ticketsService.getTicketNotes(ticketId),
        ticketsService.getTicketArtifacts(ticketId),
        ticketsService.getComments(ticketId)
      ]);

      console.log('[fetchHistory] counts', {
        history: historyData?.length,
        notes: notesData?.length,
        artifacts: artifactsData?.length,
        comments: commentsData?.length,
      });
      setHistory(historyData);
      setNotes(notesData);
      setArtifacts(artifactsData);
      setComments(commentsData);
    } catch (e: any) {
      console.error('Failed to load history:', e);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleAddActivity = async () => {
    if (!ticketId) return;
    
    try {
      if (activityType === 'comment') {
        if (!newActivity.content.trim()) return;
        
        await ticketsService.addComment(ticketId, newActivity.content);
      } else {
        if (!newActivity.title.trim()) return;
        
        // Handle file upload for file and screenshot types
        if ((newActivity.attachmentType === 'file' || newActivity.attachmentType === 'screenshot') && newActivity.file) {
          await ticketsService.uploadTicketAttachment(
            ticketId,
            newActivity.file,
            newActivity.title,
            newActivity.description,
            newActivity.attachmentType === 'file' ? 'file_attachment' : 'screenshot'
          );
        } else {
          // Handle other artifact types (link, meeting, log)
          const artifactData: any = {
            artifact_type: newActivity.attachmentType === 'link' ? 'chat_link' :
                          newActivity.attachmentType === 'meeting' ? 'meeting_summary' : 'log_file',
            title: newActivity.title,
            description: newActivity.description,
          };

          // Only add content for log files
          if (newActivity.attachmentType === 'log') {
            artifactData.content = newActivity.content;
          }

          // Only add URL for link and meeting types
          if (newActivity.attachmentType === 'link' || newActivity.attachmentType === 'meeting') {
            artifactData.url = newActivity.url;
          }

          // Add transcription for meeting types
          if (newActivity.attachmentType === 'meeting' && newActivity.transcription) {
            artifactData.content = newActivity.transcription;
          }

          await ticketsService.addTicketArtifact(ticketId, artifactData);
        }
      }
      
      // Reset form
      setNewActivity({
        content: '',
        visibility: 'internal',
        attachmentType: 'file',
        title: '',
        description: '',
        url: '',
        transcription: '',
        file: null,
      });
      setShowActivityDialog(false);
      await fetchHistory();
    } catch (e: any) {
      setError(e.message || `Failed to add ${activityType}`);
    }
  };


  const handleSave = async () => {
    if (!ticket || !ticketId) return;

    // Check if status is changing to closed or resolved
    const isClosing = (editData.status === 'closed' || editData.status === 'resolved') && 
                      ticket.status !== 'closed' && ticket.status !== 'resolved';

    if (isClosing) {
      // Generate AI summary before closing
      setGeneratingSummary(true);
      try {
        const summaryResponse = await ticketsService.generateAIClosingSummary(ticketId);
        setAiSummary(summaryResponse.summary);
        setEditableSummary(summaryResponse.summary?.summary || '');
        setShowAISummaryDialog(true);
        setGeneratingSummary(false);
        // IMPORTANT: require user approval before updating status
        return;
      } catch (e: any) {
        console.error('Failed to generate AI summary:', e);
        setGeneratingSummary(false);
        // If generation fails, we can still show an empty dialog for manual entry
        setAiSummary(null);
        setEditableSummary('');
        setShowAISummaryDialog(true);
        return;
      }
    }
    await performTicketUpdate();
  };
  const handleCancel = () => {
    if (ticket) {
      setEditData({
        status: ticket.status,
        priority: ticket.priority,
        assigned_to: ticket.assigned_to || '',
        assigned_team: ticket.metadata?.assigned_team || '',
      });
    }
    setEditing(false);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open': return <OpenIcon />;
      case 'in_progress': return <InProgressIcon />;
      case 'resolved': return <ResolvedIcon />;
      case 'closed': return <ClosedIcon />;
      default: return <OpenIcon />;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <HighPriorityIcon />;
      case 'medium': return <MediumPriorityIcon />;
      case 'low': return <LowPriorityIcon />;
      default: return <MediumPriorityIcon />;
    }
  };

  const statusColor = (status: string): string => {
    if (status === 'open') return '#6366f1';
    if (status === 'in_progress') return '#f59e0b';
    if (status === 'resolved') return '#10b981';
    if (status === 'closed') return '#64748b';
    return '#94a3b8';
  };

  const priorityColor = (priority: string): 'default' | 'error' | 'warning' => {
    if (priority === 'high') return 'error';
    if (priority === 'medium') return 'warning';
    return 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !ticket) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error || 'Ticket not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'auto',
      background: mode === 'dark' 
        ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)'
        : 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)',
    }}>
      {/* Header */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: mode === 'dark'
            ? 'rgba(10, 10, 10, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          borderBottom: mode === 'dark'
            ? '1px solid rgba(71, 85, 105, 0.3)'
            : '1px solid rgba(226, 232, 240, 0.8)',
          color: mode === 'dark' ? '#f8fafc' : '#0f172a',
        }}
      >
        <Toolbar>
          <IconButton
            onClick={() => navigate('/my-tickets')}
            sx={{
              color: mode === 'dark' ? '#f8fafc' : '#0f172a',
              '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <img
              src="/atlasai-icon.svg"
              alt="CoreSupport"
              style={{ 
                height: 36, 
                filter: 'none',
              }}
            />
            <Box>
              <Typography variant="h5" sx={{ 
                fontWeight: 700,
                background: mode === 'dark' 
                  ? 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)'
                  : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                CoreSupport Tickets
              </Typography>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}>
                Support Management System
            </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={toggleMode}
            sx={{
              color: mode === 'dark' ? '#f8fafc' : '#0f172a',
              '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
            }}
          >
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                backgroundColor: '#6366f1',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.email?.charAt(0) || 'U'}
            </Avatar>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 500,
                color: mode === 'dark' ? '#94a3b8' : '#64748b',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {user?.email?.split('@')[0] || 'User'}
            </Typography>
          </Box>

          <IconButton
            onClick={logout}
            sx={{
              color: mode === 'dark' ? '#f8fafc' : '#0f172a',
              '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        <Box sx={{ display: 'flex', gap: 4, flexDirection: 'row' }}>
          {/* Main Content */}
          <Box sx={{ 
            flex: 1,
            minWidth: 0
          }}>
            {/* Removed obsolete Summary Tabs to replace with dedicated cards below */}

            {/* Header Card */}
            <Card sx={{
              mb: 3,
              borderRadius: 4,
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark' 
                ? '1px solid rgba(99, 102, 241, 0.2)' 
                : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: mode === 'dark' 
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(99, 102, 241, 0.1)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              '&::before': mode === 'dark' ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                pointerEvents: 'none',
              } : {},
            }}>
              <CardContent sx={{ p: 5 }}>
                {/* Details Header */}
                <Typography variant="h5" sx={{ 
                    fontWeight: 700,
                  mb: 2, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                }}>
                  <AssignmentIcon sx={{ color: '#6366f1', fontSize: '1.5rem' }} />
                  Details
                </Typography>
                
                {/* Ticket Number */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600,
                    mb: 1,
                    color: mode === 'dark' ? '#94a3b8' : '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontSize: '0.75rem',
                  }}>
                    Ticket Number
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 800,
                      fontSize: '1.5rem',
                      background: mode === 'dark' 
                        ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      color: 'transparent',
                    }}>
                      {ticket.ticket_number || ticket.ticket_id.slice(0, 8)}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(ticket.ticket_number || ticket.ticket_id.slice(0, 8));
                        // You could add a toast notification here
                      }}
                      size="small"
                      sx={{
                        color: mode === 'dark' ? '#94a3b8' : '#64748b',
                        '&:hover': {
                          backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                          color: '#6366f1',
                        },
                      }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                  <Box sx={{ flex: 1 }}>
                    {/* Summary Label */}
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600,
                      mb: 1,
                      color: mode === 'dark' ? '#94a3b8' : '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '0.75rem',
                    }}>
                      Summary
                    </Typography>
                    
                    <Typography variant="h3" sx={{ 
                      fontWeight: 800,
                      mb: 2,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    background: mode === 'dark' 
                      ? 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)'
                      : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                      lineHeight: 1.2,
                  }}>
                    {ticket.summary || ticket.issue_description}
                  </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Chip
                        icon={getStatusIcon(ticket.status)}
                        label={ticket.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        sx={{
                          backgroundColor: statusColor(ticket.status),
                          color: 'white',
                          fontWeight: 700,
                          borderRadius: 3,
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          height: 'auto',
                        }}
                      />
                      <Chip
                        icon={getPriorityIcon(ticket.priority)}
                        label={ticket.priority.toUpperCase()}
                        color={priorityColor(ticket.priority)}
                        sx={{ 
                          borderRadius: 3,
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          height: 'auto',
                          fontWeight: 600,
                        }}
                      />
                      {ticket.estimated_resolution && (
                        <Chip
                          icon={<AccessTimeIcon />}
                          label={ticket.estimated_resolution}
                          sx={{
                            backgroundColor: '#10b981',
                            color: 'white',
                            fontWeight: 700,
                            borderRadius: 3,
                            px: 2,
                            py: 1,
                            fontSize: '0.875rem',
                            height: 'auto',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editing ? (
                      <>
                        <IconButton
                          onClick={handleSave}
                          sx={{
                            color: 'success.main',
                            '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.1)' }
                          }}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleCancel}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => setEditing(true)}
                        sx={{
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                {/* Description Label */}
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  mb: 1,
                  color: mode === 'dark' ? '#94a3b8' : '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontSize: '0.75rem',
                }}>
                  Description
                </Typography>

                <Typography variant="body1" sx={{ 
                  mb: 4, 
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  fontWeight: 400,
                }}>
                  {ticket.description || ticket.issue_description}
                </Typography>

                <Divider sx={{ 
                  my: 4,
                  borderColor: mode === 'dark' 
                    ? 'rgba(71, 85, 105, 0.3)' 
                    : 'rgba(226, 232, 240, 0.8)',
                }} />

                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 3,
                  mb: 2,
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' 
                      ? 'rgba(15, 15, 15, 0.6)' 
                      : 'rgba(248, 250, 252, 0.8)',
                  }}>
                    <ScheduleIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        CREATED
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(() => {
                          const date = new Date(ticket.created_at);
                          return date.toLocaleString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          });
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: mode === 'dark' 
                      ? 'rgba(15, 15, 15, 0.6)' 
                      : 'rgba(248, 250, 252, 0.8)',
                  }}>
                    <ScheduleIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        UPDATED
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(() => {
                          const date = new Date(ticket.updated_at);
                          return date.toLocaleString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          });
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {ticket.estimated_resolution && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' 
                        ? 'rgba(15, 23, 42, 0.5)' 
                        : 'rgba(248, 250, 252, 0.8)',
                    }}>
                      <AccessTimeIcon sx={{ color: '#10b981', fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          ESTIMATED RESOLUTION
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ticket.estimated_resolution}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Additional Information Card */}
            <Card sx={{
              mb: 3,
              borderRadius: 4,
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark' 
                ? '1px solid rgba(99, 102, 241, 0.2)' 
                : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: mode === 'dark' 
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(99, 102, 241, 0.1)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              '&::before': mode === 'dark' ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                pointerEvents: 'none',
              } : {},
            }}>
              <CardContent sx={{ p: 5 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  mb: 4, 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2,
                  background: mode === 'dark' 
                    ? 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)'
                    : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  <InfoIcon sx={{ fontSize: 28 }} />
                  Additional Information
                </Typography>
                
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
                  {/* Requested By */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon fontSize="small" />
                      Requested By
                    </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ 
                        width: 24, 
                        height: 24, 
                        bgcolor: '#6366f1',
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        {(ticket.metadata?.requested_by || ticket.metadata?.user_name || 'U')[0]?.toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ticket.metadata?.requested_by || ticket.metadata?.user_email || 'Unknown'}
                    </Typography>
                  </Box>
                  </Box>
                  {/* Category */}
                  {ticket.metadata?.category && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" />
                        Category
                      </Typography>
                      <Chip
                        label={ticket.metadata.category}
                        size="small"
                        sx={{
                          backgroundColor: '#6366f1',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}

                  {/* Assigned Team */}
                  {ticket.metadata?.assigned_team && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon fontSize="small" />
                        Assigned Team
                      </Typography>
                      <Chip
                        label={ticket.metadata.assigned_team}
                        size="small"
                        sx={{
                          backgroundColor: '#8b5cf6',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}

                  {/* Assigned To */}
                  {ticket.assigned_to && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        Assigned To
                      </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ 
                          width: 24, 
                          height: 24, 
                          bgcolor: '#6366f1',
                          fontSize: 12,
                          fontWeight: 600,
                        }}>
                          {ticket.assigned_to[0]?.toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ticket.assigned_to}
                    </Typography>
                  </Box>
                    </Box>
                  )}

                  {/* AI Escalation Badge */}
                  {ticket.metadata?.ai_escalated && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      mt: 2,
                      p: 2,
                      borderRadius: 2,
                      background: mode === 'dark' 
                        ? 'rgba(99, 102, 241, 0.1)' 
                        : 'rgba(99, 102, 241, 0.05)',
                      border: mode === 'dark' 
                        ? '1px solid rgba(99, 102, 241, 0.3)' 
                        : '1px solid rgba(99, 102, 241, 0.2)',
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}>
                        AI
                      </Box>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 600,
                        color: mode === 'dark' ? '#a5b4fc' : '#6366f1'
                      }}>
                        AI Escalated: {ticket.metadata.escalation_reason === 'urgency_detected' 
                          ? 'Urgent deadline detected'
                          : ticket.metadata.escalation_reason === 'sentiment_detected'
                          ? 'High urgency sentiment'
                          : ticket.metadata.escalation_reason
                        }
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Activity Section */}
            <Card sx={{
              mb: 3,
              borderRadius: 4,
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark' 
                ? '1px solid rgba(99, 102, 241, 0.2)' 
                : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: mode === 'dark' 
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(99, 102, 241, 0.1)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              '&::before': mode === 'dark' ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                pointerEvents: 'none',
              } : {},
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700,
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    background: mode === 'dark' 
                      ? 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)'
                      : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    <TimelineIcon sx={{ fontSize: 28 }} />
                    Activity
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<ChatIcon />}
                      onClick={(e) => {
                        (e.currentTarget as HTMLButtonElement).blur();
                        console.log('[CommentButton] click -> open dialog');
                        setActivityType('comment');
                        setShowActivityDialog(true);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        backgroundColor: '#6366f1',
                        '&:hover': {
                          backgroundColor: '#5b5bd6',
                        },
                      }}
                    >
                      Add Comment
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AttachFileIcon />}
                      onClick={() => {
                        setActivityType('attachment');
                        setShowActivityDialog(true);
                      }}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.5)' : '#6366f1',
                        color: mode === 'dark' ? '#a5b4fc' : '#6366f1',
                        '&:hover': {
                          borderColor: '#6366f1',
                          backgroundColor: mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                        },
                      }}
                    >
                      Add Attachment
                    </Button>
                  </Box>
          </Box>

                {loadingHistory ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Combined Timeline */}
                    {[...history, ...notes, ...artifacts, ...comments].length === 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 6,
                        px: 4,
                      }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: mode === 'dark' 
                            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                          border: mode === 'dark' 
                            ? '1px solid rgba(99, 102, 241, 0.2)' 
                            : '1px solid rgba(99, 102, 241, 0.1)',
                          mb: 3,
                        }}>
                          <TimelineIcon sx={{ 
                            fontSize: 40, 
                            color: mode === 'dark' ? '#a5b4fc' : '#6366f1',
                          }} />
                        </Box>
                        
                        <Typography variant="h6" sx={{ 
                          fontWeight: 700, 
                          mb: 1,
                          color: mode === 'dark' ? '#f8fafc' : '#0f172a',
                          textAlign: 'center',
                        }}>
                          No Activity Yet
                        </Typography>
                        
                        <Typography variant="body2" sx={{
                          color: mode === 'dark' ? '#94a3b8' : '#64748b',
                          textAlign: 'center',
                          maxWidth: 300,
                          lineHeight: 1.6,
                        }}>
                          Add notes or artifacts to track progress on this ticket
                        </Typography>
                      </Box>
                    ) : (
                      [
                        // Add ticket creation event
                        {
                          event_type: 'created',
                          description: `Ticket created: ${ticket.issue_description.substring(0, 100)}${ticket.issue_description.length > 100 ? '...' : ''}`,
                          author_email: ticket.metadata?.user_email || ticket.metadata?.requested_by || 'Unknown',
                          author_id: ticket.metadata?.user_id || 'Unknown',
                          timestamp: ticket.created_at,
                          metadata: { 
                            priority: ticket.priority,
                            status: ticket.status,
                            created_from_chat: ticket.metadata?.created_from_chat || false
                          }
                        },
                        ...history, 
                        ...notes, 
                        ...artifacts,
                        ...comments.map(comment => ({
                          event_type: 'comment',
                          description: comment.comment,
                          author_email: comment.author_email,
                          author_id: comment.author_id,
                          timestamp: comment.created_at,
                          metadata: { type: 'comment' }
                        }))
                      ]
                        .sort((a, b) => new Date(a.timestamp || a.created_at).getTime() - new Date(b.timestamp || b.created_at).getTime())
                        .map((item, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            p: 3,
              borderRadius: 3,
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(15, 15, 15, 0.5)' 
                              : 'rgba(248, 250, 252, 0.5)',
                            border: mode === 'dark' 
                              ? '1px solid rgba(71, 85, 105, 0.2)' 
                              : '1px solid rgba(226, 232, 240, 0.5)',
                          }}>
                            <Box sx={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: '50%', 
                              backgroundColor: item.event_type === 'comment' ? '#8b5cf6' : '#6366f1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              {item.event_type === 'comment' ? (
                                <ChatIcon sx={{ color: 'white', fontSize: 20 }} />
                              ) : item.event_type ? (
                                <TimelineIcon sx={{ color: 'white', fontSize: 20 }} />
                              ) : item.content ? (
                                <NoteIcon sx={{ color: 'white', fontSize: 20 }} />
                              ) : (
                                <AttachFileIcon sx={{ color: 'white', fontSize: 20 }} />
                              )}
                            </Box>
                            
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {item.event_type ? 
                                    item.description : 
                                    item.content ? 
                                      'Note Added' : 
                                      'Artifact Attached'
                                  }
                                </Typography>
                                
                                {item.event_type && (
                                  <Chip
                                    label={item.event_type.replace('_', ' ')}
                                    size="small"
                                    sx={{
                                      backgroundColor: '#6366f1',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                                
                                {item.visibility && (
                                  <Chip
                                    icon={item.visibility === 'public' ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                    label={item.visibility}
                                    size="small"
                                    sx={{
                                      backgroundColor: item.visibility === 'public' ? '#10b981' : '#f59e0b',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      height: 20,
                                    }}
                                  />
                                )}
                              </Box>
                              
                              {item.content && (
                                <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
                                  {item.content}
                                </Typography>
                              )}
                              
                              {item.title && (
                                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                  {item.title}
                                </Typography>
                              )}
                              
                              {item.description && (
                                <Typography variant="body2" sx={{ mb: 1, color: mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                                  {item.description}
                                </Typography>
                              )}
                              
                              {(item.old_value || item.new_value) && (
                                <Box sx={{ 
                                  backgroundColor: mode === 'dark' ? 'rgba(15, 15, 15, 0.8)' : 'rgba(248, 250, 252, 0.8)',
                                  borderRadius: 2,
                                  p: 2,
                                  mt: 1,
                                }}>
                                  {item.old_value && (
                                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: 600 }}>
                                      From: {item.old_value}
                                    </Typography>
                                  )}
                                  {item.old_value && item.new_value && <br />}
                                  {item.new_value && (
                                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600 }}>
                                      To: {item.new_value}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <Avatar sx={{ 
                                  width: 20, 
                                  height: 20, 
                                  bgcolor: '#6366f1',
                                  fontSize: 10,
                                }}>
                                  {(item.author_email || item.author_id || 'U')[0]?.toUpperCase()}
                                </Avatar>
                                <Typography variant="caption" sx={{ color: mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                                  {item.author_email || item.author_id || 'Unknown'} • {new Date(item.timestamp || item.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Closing Summary Tab Content */}
            {false && (
              <Box>
            <Card sx={{
                  mb: 3,
                  borderRadius: 4,
              background: mode === 'dark' 
                    ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
                  border: mode === 'dark' 
                    ? '1px solid rgba(99, 102, 241, 0.2)' 
                    : '1px solid rgba(226, 232, 240, 0.8)',
                  boxShadow: mode === 'dark' 
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(99, 102, 241, 0.1)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  '&::before': mode === 'dark' ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                    pointerEvents: 'none',
                  } : {},
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700,
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        background: mode === 'dark' 
                          ? 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)'
                          : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}>
                        <TimelineIcon sx={{ fontSize: 28 }} />
                        Ticket History
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<ChatIcon />}
                          onClick={(e) => {
                            (e.currentTarget as HTMLButtonElement).blur();
                            console.log('[CommentButton-secondary] click -> open dialog');
                            setActivityType('comment');
                            setShowActivityDialog(true);
                          }}
                          sx={{
                            backgroundColor: '#6366f1',
                            borderRadius: 3,
                            px: 3,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                              backgroundColor: '#5b5bd6',
                            },
                          }}
                        >
                          Add Comment
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<AttachFileIcon />}
                          onClick={() => {
                            setActivityType('attachment');
                            setShowActivityDialog(true);
                          }}
                          sx={{
                            borderColor: '#8b5cf6',
                            color: '#8b5cf6',
                            borderRadius: 3,
                            px: 3,
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: '#7c3aed',
                              backgroundColor: 'rgba(139, 92, 246, 0.1)',
                            },
                          }}
                        >
                          Add Attachment
                        </Button>
                      </Box>
                    </Box>

                    {loadingHistory ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Combine history events, notes, and artifacts, sort by timestamp */}
                        {[...history, ...notes, ...artifacts]
                          .filter((item) => {
                            // Filter out internal comments for non-admin users
                            if (item.note_id && item.visibility === 'internal' && !user?.is_admin) {
                              return false;
                            }
                            return true;
                          })
                          .sort((a, b) => new Date(b.timestamp || b.created_at).getTime() - new Date(a.timestamp || a.created_at).getTime())
                          .map((item, index) => (
                            <Box
                              key={item.event_id || item.note_id || item.artifact_id || index}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                p: 3,
                                borderRadius: 3,
                                backgroundColor: mode === 'dark' 
                                  ? 'rgba(15, 15, 15, 0.6)' 
                                  : 'rgba(248, 250, 252, 0.8)',
                                border: mode === 'dark' 
                                  ? '1px solid rgba(71, 85, 105, 0.2)' 
                                  : '1px solid rgba(226, 232, 240, 0.5)',
                              }}
                            >
                              {/* Timeline dot */}
                              <Box sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: item.note_id ? '#8b5cf6' : item.artifact_id ? '#10b981' : '#6366f1',
                                mt: 1,
                                flexShrink: 0,
                              }} />
                              
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {item.note_id ? 'Note Added' : item.artifact_id ? `Artifact: ${item.title}` : item.description}
                                  </Typography>
                                  <Chip
                                    label={item.note_id ? 'Note' : item.artifact_id ? 'Artifact' : item.event_type?.replace(/_/g, ' ')}
                                    size="small"
                                    sx={{
                                      backgroundColor: item.note_id ? '#8b5cf6' : item.artifact_id ? '#10b981' : '#6366f1',
                                      color: 'white',
                                      fontWeight: 600,
                                      fontSize: '0.75rem',
                                    }}
                                  />
                                  {item.artifact_id && item.artifact_type && (
                                    <Chip
                                      label={item.artifact_type.replace(/_/g, ' ')}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.75rem',
                                        borderColor: mode === 'dark' ? '#475569' : '#cbd5e1',
                                        color: mode === 'dark' ? '#94a3b8' : '#64748b',
                                      }}
                                    />
                                  )}
                                  {item.note_id && item.visibility && (
                                    <Chip
                                      icon={item.visibility === 'internal' ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                      label={item.visibility}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        fontSize: '0.75rem',
                                        borderColor: mode === 'dark' ? '#475569' : '#cbd5e1',
                                        color: mode === 'dark' ? '#94a3b8' : '#64748b',
                                      }}
                                    />
                                  )}
                                </Box>
                                
                                {item.note_id && (
                                  <Typography variant="body2" sx={{ 
                                    mb: 2, 
                                    color: 'text.secondary',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                  }}>
                                    {item.content}
                                  </Typography>
                                )}
                                
                                {item.artifact_id && (
                                  <Box sx={{ mb: 2 }}>
                                    {item.description && (
                                      <Typography variant="body2" sx={{ 
                                        mb: 1, 
                                        color: 'text.secondary',
                                        lineHeight: 1.6,
                                      }}>
                                        {item.description}
                                      </Typography>
                                    )}
                                    {item.content && (
                                      <Typography variant="body2" sx={{ 
                                        mb: 1, 
                                        color: 'text.secondary',
                                        lineHeight: 1.6,
                                        whiteSpace: 'pre-wrap',
                                        backgroundColor: mode === 'dark' 
                                          ? 'rgba(15, 23, 42, 0.5)' 
                                          : 'rgba(248, 250, 252, 0.8)',
                                        padding: 2,
                                        borderRadius: 2,
              border: mode === 'dark' 
                ? '1px solid rgba(71, 85, 105, 0.3)' 
                                          : '1px solid rgba(226, 232, 240, 0.5)',
                                      }}>
                                        {item.content}
                                      </Typography>
                                    )}
                                    {item.url && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinkIcon sx={{ fontSize: 16, color: '#6366f1' }} />
                                        <Typography 
                                          variant="body2" 
                                          component="a" 
                                          href={item.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          sx={{ 
                                            color: '#6366f1',
                                            textDecoration: 'none',
                                            '&:hover': { textDecoration: 'underline' },
                                          }}
                                        >
                                          {item.url}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                                
                                {item.old_value && item.new_value && (
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    mb: 1,
                                    p: 1,
                                    borderRadius: 2,
                                    backgroundColor: mode === 'dark' 
                                      ? 'rgba(15, 23, 42, 0.5)' 
                                      : 'rgba(248, 250, 252, 0.8)',
                                  }}>
                                    <Typography variant="caption" sx={{ 
                                      textDecoration: 'line-through',
                                      color: 'error.main',
                                      fontWeight: 500,
                                    }}>
                                      {item.old_value}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      →
                                    </Typography>
                                    <Typography variant="caption" sx={{ 
                                      color: 'success.main',
                                      fontWeight: 500,
                                    }}>
                                      {item.new_value}
                                    </Typography>
                                  </Box>
                                )}
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Avatar sx={{ 
                                    width: 24, 
                                    height: 24, 
                                    bgcolor: '#6366f1',
                                    fontSize: 12,
                                    fontWeight: 600,
                                  }}>
                                    {item.author_email?.[0]?.toUpperCase() || 'U'}
                                  </Avatar>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.author_email}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    •
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {new Date(item.timestamp || item.created_at).toLocaleString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        
                        {history.length === 0 && notes.length === 0 && artifacts.length === 0 && (
                          <Box sx={{ 
                            textAlign: 'center', 
                            py: 6,
                            color: 'text.secondary',
                          }}>
                            <TimelineIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              No history yet
                            </Typography>
                            <Typography variant="body2">
                              Ticket events and notes will appear here
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>

          {/* Sidebar - Stacked Content */}
          <Box sx={{ 
            width: 380, 
            minWidth: 380,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}>
            {/* Ticket Details Card */}
            <Card sx={{
              borderRadius: 4,
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark' 
                ? '1px solid rgba(99, 102, 241, 0.2)' 
                : '1px solid rgba(226, 232, 240, 0.8)',
              boxShadow: mode === 'dark' 
                ? '0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(99, 102, 241, 0.1)'
                : '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
              position: 'relative',
              '&::before': mode === 'dark' ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 4,
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                pointerEvents: 'none',
              } : {},
            }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700,
                    background: mode === 'dark' 
                      ? 'linear-gradient(135deg, #f8fafc 0%, #cbd5e1 100%)'
                      : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                  Ticket Details
                </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {editing ? (
                      <>
                        <IconButton
                          onClick={handleSave}
                          sx={{
                            color: 'success.main',
                            '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.1)' }
                          }}
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleCancel}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                          }}
                        >
                          <CancelIcon />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => setEditing(true)}
                        sx={{
                          color: mode === 'dark' ? '#94a3b8' : '#64748b',
                          '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Status */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Status
                    </Typography>
                    {editing ? (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={editData.status}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          setEditData(prev => ({ ...prev, status: newStatus }));
                          
                          // If moving to in_progress and no assignment, show assignment dialog
                          if (newStatus === 'in_progress' && !editData.assigned_to) {
                            setShowAssignmentDialog(true);
                          }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(15, 15, 15, 0.8)' 
                              : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                          }
                        }}
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="in_progress">In Progress</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </TextField>
                    ) : (
                      <Chip
                        icon={getStatusIcon(ticket.status)}
                        label={ticket.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        sx={{
                          backgroundColor: statusColor(ticket.status),
                          color: 'white',
                          fontWeight: 700,
                          borderRadius: 3,
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          height: 'auto',
                        }}
                      />
                    )}
                  </Box>

                  {/* Priority */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Priority
                    </Typography>
                    {editing ? (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={editData.priority}
                        onChange={(e) => setEditData(prev => ({ ...prev, priority: e.target.value }))}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(15, 15, 15, 0.8)' 
                              : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                          }
                        }}
                      >
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </TextField>
                    ) : (
                      <Chip
                        icon={getPriorityIcon(ticket.priority)}
                        label={ticket.priority.toUpperCase()}
                        color={priorityColor(ticket.priority)}
                        sx={{ 
                          borderRadius: 3,
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          height: 'auto',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>

                  {/* Assigned To */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Assigned To
                    </Typography>
                    {editing ? (
                      <Autocomplete
                        size="small"
                        fullWidth
                        loading={usersLoading}
                        options={users}
                        filterOptions={(options, state) => {
                          const q = state.inputValue.toLowerCase().trim();
                          if (!q) {
                            // Show first 10 users when no search term
                            return options.slice(0, 10);
                          }
                          
                          // Smart search: prioritize exact email matches, then partial matches
                          const filtered = options.filter(o => {
                            const email = o.email?.toLowerCase() || '';
                            const name = o.name?.toLowerCase() || '';
                            return email.includes(q) || name.includes(q);
                          });
                          
                          // Sort by relevance: exact email matches first, then partial matches
                          filtered.sort((a, b) => {
                            const aEmail = a.email?.toLowerCase() || '';
                            const bEmail = b.email?.toLowerCase() || '';
                            const aName = a.name?.toLowerCase() || '';
                            const bName = b.name?.toLowerCase() || '';
                            
                            // Exact email match gets highest priority
                            if (aEmail === q && bEmail !== q) return -1;
                            if (bEmail === q && aEmail !== q) return 1;
                            
                            // Email starts with query gets second priority
                            if (aEmail.startsWith(q) && !bEmail.startsWith(q)) return -1;
                            if (bEmail.startsWith(q) && !aEmail.startsWith(q)) return 1;
                            
                            // Name starts with query gets third priority
                            if (aName.startsWith(q) && !bName.startsWith(q)) return -1;
                            if (bName.startsWith(q) && !aName.startsWith(q)) return 1;
                            
                            return 0;
                          });
                          
                          // Limit to 20 results for performance
                          return filtered.slice(0, 20);
                        }}
                        getOptionLabel={(opt: OrgUser) => opt?.email || ''}
                        value={users.find(u => u.email === editData.assigned_to) || null}
                        onChange={(_, val: OrgUser | null) => setEditData(prev => ({ ...prev, assigned_to: val?.email || '' }))}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search user by email or name"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <SearchIcon color="action" sx={{ mr: 1 }} />
                              ),
                            }}
                          />
                        )}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(15, 15, 15, 0.8)' 
                              : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                          }
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ticket.assigned_to ? (
                          <>
                            <Avatar sx={{ 
                              width: 32, 
                              height: 32, 
                              bgcolor: '#6366f1',
                              fontSize: 14,
                              fontWeight: 600,
                            }}>
                              {ticket.assigned_to[0]?.toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">
                              {ticket.assigned_to}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Assigned Team */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Assigned Team
                    </Typography>
                    {editing ? (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="team-name"
                        value={editData.assigned_team}
                        onChange={(e) => setEditData(prev => ({ ...prev, assigned_team: e.target.value }))}
                        InputProps={{
                          startAdornment: (
                            <GroupIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: mode === 'dark' 
                              ? 'rgba(15, 15, 15, 0.8)' 
                              : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                          }
                        }}
                      />
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ticket.metadata?.assigned_team ? (
                          <>
                            <GroupIcon sx={{ color: mode === 'dark' ? '#94a3b8' : '#64748b' }} />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {ticket.metadata.assigned_team}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No team assigned
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Estimated Resolution */}
                  {ticket.estimated_resolution && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Estimated Resolution
                      </Typography>
                      <Chip
                        label={ticket.estimated_resolution}
                        sx={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          fontWeight: 600,
                          borderRadius: 2,
                        }}
                      />
                    </Box>
                  )}

                  {/* Created By */}
                  {ticket.metadata?.user_name && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Created By
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: '#6366f1',
                          fontSize: 14,
                          fontWeight: 600,
                        }}>
                          {ticket.metadata.user_name[0]?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {ticket.metadata.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ticket.metadata.user_email}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Source */}
                  {ticket.metadata?.created_from_chat && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Source
                      </Typography>
                      <Chip
                        label="AI Chat"
                        size="small"
                        sx={{
                          backgroundColor: '#8b5cf6',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  )}

                  {/* Ticket Number */}
                  {ticket.ticket_number && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Ticket Number
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'monospace',
                      color: 'text.secondary',
                      backgroundColor: mode === 'dark' 
                          ? 'rgba(15, 15, 15, 0.6)' 
                        : 'rgba(248, 250, 252, 0.8)',
                      padding: 1,
                      borderRadius: 1,
                    }}>
                        {ticket.ticket_number}
                    </Typography>
                  </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* Assignment Dialog */}
      {showAssignmentDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowAssignmentDialog(false)}
        >
          <Card
            sx={{
              p: 4,
              maxWidth: 400,
              width: '90%',
              borderRadius: 3,
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark' 
                ? '1px solid rgba(71, 85, 105, 0.3)' 
                : '1px solid rgba(226, 232, 240, 0.8)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              Assign Ticket
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              To move this ticket to "In Progress", please assign it to a team member.
            </Typography>

            <TextField
              fullWidth
              label="Assign to"
              placeholder="Enter team member name or email"
              value={editData.assigned_to}
              onChange={(e) => setEditData(prev => ({ ...prev, assigned_to: e.target.value }))}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: mode === 'dark' 
                    ? 'rgba(15, 15, 15, 0.8)' 
                    : 'rgba(255, 255, 255, 0.8)',
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                },
                '& .MuiOutlinedInput-input': {
                  color: 'text.primary',
                },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <IconButton
                onClick={() => {
                  setShowAssignmentDialog(false);
                  setEditData(prev => ({ ...prev, status: 'open' }));
                }}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' }
                }}
              >
                <CancelIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  if (editData.assigned_to.trim()) {
                    setShowAssignmentDialog(false);
                  }
                }}
                disabled={!editData.assigned_to.trim()}
                sx={{
                  color: 'primary.main',
                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)' },
                  '&:disabled': { color: 'text.disabled' }
                }}
              >
                <SaveIcon />
              </IconButton>
            </Box>
          </Card>
        </Box>
      )}



        {/* Comment Dialog */}
        <Dialog
          open={showActivityDialog && activityType === 'comment'}
          onClose={() => setShowActivityDialog(false)}
          TransitionProps={{
            onEntering: () => {
              // Defer to next frame to ensure textarea is in the DOM
              requestAnimationFrame(() => commentInputRef.current?.focus());
            }
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark'
                ? '1px solid rgba(71, 85, 105, 0.3)'
                : '1px solid rgba(226, 232, 240, 0.8)',
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          }}>
            <ChatIcon />
            Add Comment
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <TextField
                multiline
                rows={4}
                fullWidth
                inputRef={commentInputRef}
                label="Comment"
                placeholder="Share your thoughts or updates on this ticket..."
                value={newActivity.content}
                onChange={(e) => {
                  console.log('[CommentInput] change length=', e.target.value.length);
                  setNewActivity(prev => ({ ...prev, content: e.target.value }))
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: mode === 'dark'
                      ? 'rgba(39, 39, 42, 0.6) !important'
                      : 'rgba(255, 255, 255, 0.95) !important',
                    '&.Mui-focused': {
                      backgroundColor: mode === 'dark'
                        ? 'rgba(39, 39, 42, 0.6) !important'
                        : 'rgba(255, 255, 255, 0.95) !important',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                  '& textarea': {
                    color: mode === 'dark' ? '#ffffff' : '#0f172a',
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
                }}>
                  Visibility
                </InputLabel>
                <Select
                  value={newActivity.visibility}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, visibility: e.target.value as any }))}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: mode === 'dark'
                      ? 'rgba(15, 15, 15, 0.8)'
                      : 'rgba(255, 255, 255, 0.8)',
                    '& .MuiOutlinedInput-input': {
                      color: 'text.primary',
                    },
                  }}
                >
                  <MenuItem value="internal">Internal (Team only)</MenuItem>
                  <MenuItem value="public">Public (Visible to requester)</MenuItem>
                  <MenuItem value="private">Private (Admin only)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setShowActivityDialog(false)}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddActivity}
              disabled={!newActivity.content.trim()}
              variant="contained"
              sx={{
                backgroundColor: '#6366f1',
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#5b5bd6',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(99, 102, 241, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Add Comment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Attachment Dialog */}
        <Dialog
          open={showActivityDialog && activityType === 'attachment'}
          onClose={() => setShowActivityDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark'
                ? '1px solid rgba(71, 85, 105, 0.3)'
                : '1px solid rgba(226, 232, 240, 0.8)',
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          }}>
            <AttachFileIcon />
            Add Attachment
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <>
                <FormControl fullWidth>
                  <InputLabel sx={{ 
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' 
                  }}>
                    Attachment Type
                  </InputLabel>
                  <Select
                    value={newActivity.attachmentType}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, attachmentType: e.target.value as any }))}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' 
                        ? 'rgba(15, 15, 15, 0.8)' 
                        : 'rgba(255, 255, 255, 0.8)',
                      '& .MuiOutlinedInput-input': {
                        color: 'text.primary',
                      },
                    }}
                  >
                    <MenuItem value="file">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon fontSize="small" />
                        File Upload
                      </Box>
                    </MenuItem>
                    <MenuItem value="link">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon fontSize="small" />
                        Link/URL
                      </Box>
                    </MenuItem>
                    <MenuItem value="meeting">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideoCallIcon fontSize="small" />
                        Meeting Summary
                      </Box>
                    </MenuItem>
                    <MenuItem value="screenshot">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ImageIcon fontSize="small" />
                        Screenshot
                      </Box>
                    </MenuItem>
                    <MenuItem value="log">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BugReportIcon fontSize="small" />
                        Log File
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Title"
                  placeholder="Enter attachment title..."
                  value={newActivity.title}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, title: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' 
                        ? 'rgba(15, 15, 15, 0.8)' 
                        : 'rgba(255, 255, 255, 0.8)',
                    },
                    '& .MuiInputLabel-root': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'text.primary',
                    },
                  }}
                />
                
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Brief description of the attachment..."
                  value={newActivity.description}
                  onChange={(e) => setNewActivity(prev => ({ ...prev, description: e.target.value }))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: mode === 'dark' 
                        ? 'rgba(15, 15, 15, 0.8)' 
                        : 'rgba(255, 255, 255, 0.8)',
                    },
                    '& .MuiInputLabel-root': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'text.primary',
                    },
                  }}
                />

                {(newActivity.attachmentType === 'file' || newActivity.attachmentType === 'screenshot') && (
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        py: 1.5,
                        borderStyle: 'dashed',
                        borderWidth: 2,
                      }}
                    >
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setNewActivity(prev => ({ ...prev, file }));
                          }
                        }}
                      />
                      {newActivity.file ? newActivity.file.name : 'Choose File'}
                    </Button>
                  </Box>
                )}

                {(newActivity.attachmentType === 'link' || newActivity.attachmentType === 'meeting') && (
                  <TextField
                    fullWidth
                    label="URL"
                    placeholder="Enter the URL..."
                    value={newActivity.url}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, url: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: mode === 'dark' 
                          ? 'rgba(15, 15, 15, 0.8)' 
                          : 'rgba(255, 255, 255, 0.8)',
                      },
                      '& .MuiInputLabel-root': {
                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      },
                      '& .MuiOutlinedInput-input': {
                        color: 'text.primary',
                      },
                    }}
                  />
                )}

                {newActivity.attachmentType === 'meeting' && (
                  <TextField
                    multiline
                    rows={6}
                    fullWidth
                    label="Meeting Transcription"
                    placeholder="Paste the meeting transcription here..."
                    value={newActivity.transcription}
                    onChange={(e) => setNewActivity(prev => ({ ...prev, transcription: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: mode === 'dark'
                          ? 'rgba(15, 15, 15, 0.8)'
                          : 'rgba(255, 255, 255, 0.8)',
                      },
                      '& .MuiInputLabel-root': {
                        color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                      },
                      '& .MuiOutlinedInput-input': {
                        color: 'text.primary',
                      },
                    }}
                  />
                )}

                  {newActivity.attachmentType === 'log' && (
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      label="Log Content"
                      placeholder="Enter log file content..."
                      value={newActivity.content}
                      onChange={(e) => setNewActivity(prev => ({ ...prev, content: e.target.value }))}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: mode === 'dark'
                            ? 'rgba(15, 15, 15, 0.8)'
                            : 'rgba(255, 255, 255, 0.8)',
                        },
                        '& .MuiInputLabel-root': {
                          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'text.primary',
                        },
                      }}
                    />
                  )}
              </>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setShowActivityDialog(false)}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddActivity}
              disabled={
                !newActivity.title.trim() || 
                (newActivity.attachmentType === 'log' && !newActivity.content.trim())
              }
              variant="contained"
              sx={{
                backgroundColor: '#6366f1',
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#5b5bd6',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(99, 102, 241, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Add Attachment
            </Button>
          </DialogActions>
        </Dialog>

        {/* AI Summary Dialog */}
        <Dialog
          open={showAISummaryDialog}
          onClose={() => !generatingSummary && setShowAISummaryDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: mode === 'dark'
                ? 'linear-gradient(135deg, rgba(15, 15, 15, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
              border: mode === 'dark'
                ? '1px solid rgba(71, 85, 105, 0.3)'
                : '1px solid rgba(226, 232, 240, 0.8)',
            }
          }}
        >
          <DialogTitle sx={{
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: mode === 'dark' ? '#f8fafc' : '#0f172a',
          }}>
            <SummarizeIcon />
            {generatingSummary ? 'Generating AI Summary...' : 'Review AI Summary & Close Ticket'}
          </DialogTitle>
          <DialogContent>
            {generatingSummary ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Analyzing ticket history, comments, and resolution...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Our AI has generated a summary of this ticket. You can review and edit it before closing.
                </Typography>
                <TextField
                  multiline
                  rows={8}
                  fullWidth
                  label="AI Summary & After Action Report"
                  placeholder="Summary will appear here..."
                  value={editableSummary}
                  onChange={(e) => setEditableSummary(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: mode === 'dark'
                        ? 'rgba(39, 39, 42, 0.6) !important'
                        : 'rgba(255, 255, 255, 0.95) !important',
                      '&.Mui-focused': {
                        backgroundColor: mode === 'dark'
                          ? 'rgba(39, 39, 42, 0.6) !important'
                          : 'rgba(255, 255, 255, 0.95) !important',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiOutlinedInput-input': {
                      color: mode === 'dark' ? '#ffffff' : '#0f172a',
                    },
                    '& textarea': {
                      color: mode === 'dark' ? '#ffffff' : '#0f172a',
                    },
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => {
                setShowAISummaryDialog(false);
                setAiSummary(null);
                setEditableSummary('');
              }}
              disabled={generatingSummary}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!ticketId) return;
                setGeneratingSummary(true);
                try {
                  const summaryResponse = await ticketsService.generateAIClosingSummary(ticketId);
                  setAiSummary(summaryResponse.summary);
                  setEditableSummary(summaryResponse.summary?.summary || '');
                } catch (e) {
                  console.error('Failed to regenerate AI summary:', e);
                } finally {
                  setGeneratingSummary(false);
                }
              }}
              disabled={generatingSummary}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Regenerate
            </Button>
            <Button
              onClick={performTicketUpdate}
              disabled={generatingSummary || !editableSummary.trim()}
              variant="contained"
              sx={{
                backgroundColor: '#6366f1',
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#4f46e5',
                },
              }}
            >
              Close Ticket with Summary
            </Button>
          </DialogActions>
        </Dialog>

        {/* AI Closing Summary Card - Modern Design */}
        {(() => {
          const ai = ticket?.metadata?.ai_summary as any;
          const hasText = typeof ai === 'string' ? !!ai.trim() : !!ai?.text?.trim();
          return hasText;
        })() && (
          <Card 
            sx={{ 
              mt: 3, 
              borderRadius: 4, 
              overflow: 'hidden',
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
              border: mode === 'dark' ? '1px solid rgba(99, 102, 241, 0.2)' : '1px solid rgba(99, 102, 241, 0.15)',
              boxShadow: mode === 'dark' 
                ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 4px 20px rgba(99, 102, 241, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: mode === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.4)'
                  : '0 8px 30px rgba(99, 102, 241, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {/* Header with gradient background */}
            <Box 
              sx={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                p: 3,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box 
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                      p: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <SummarizeIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'white',
                        letterSpacing: '-0.02em',
                        fontSize: '1.5rem'
                      }}
                    >
                      AI Closing Summary
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      Generated by CoreSupport Agent
                    </Typography>
                  </Box>
                </Box>
                {(() => {
                  const ai = ticket?.metadata?.ai_summary as any;
                  return typeof ai === 'object' && ai?.updated_at;
                })() && (
                  <Chip
                    label={(() => {
                      const ai = ticket?.metadata?.ai_summary as any;
                      return new Date(ai.updated_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                    })()}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      backdropFilter: 'blur(10px)'
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: 4 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  fontSize: '0.95rem',
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                }}
              >
                {(() => {
                  const ai = ticket?.metadata?.ai_summary as any;
                  return typeof ai === 'string' ? ai : (ai?.text || '');
                })()}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* AI After Action Report (AAR) Card - Modern Design */}
        {(() => {
          const ai = ticket?.metadata?.ai_summary as any;
          const raw = typeof ai === 'object' ? ai?.raw : undefined;
          const hasAAR = raw && (raw.root_cause || raw.resolution || raw.lessons_learned || raw.prevention || raw.recommendations || raw.timeline || raw.technical_insights);
          return Boolean(hasAAR);
        })() && (
          <Card 
            sx={{ 
              mt: 3, 
              borderRadius: 4, 
              overflow: 'hidden',
              background: mode === 'dark' 
                ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(236, 72, 153, 0.03) 0%, rgba(239, 68, 68, 0.03) 100%)',
              border: mode === 'dark' ? '1px solid rgba(236, 72, 153, 0.2)' : '1px solid rgba(236, 72, 153, 0.15)',
              boxShadow: mode === 'dark' 
                ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 4px 20px rgba(236, 72, 153, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: mode === 'dark'
                  ? '0 8px 30px rgba(0, 0, 0, 0.4)'
                  : '0 8px 30px rgba(236, 72, 153, 0.12)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {/* Header with gradient background */}
            <Box 
              sx={{ 
                background: 'linear-gradient(135deg, #ec4899 0%, #ef4444 100%)',
                p: 3,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box 
                  sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <NoteIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700, 
                      color: 'white',
                      letterSpacing: '-0.02em',
                      fontSize: '1.5rem'
                    }}
                  >
                    After Action Report
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    Detailed Analysis & Insights
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: 4 }}>

              {(() => {
                const ai = ticket?.metadata?.ai_summary as any;
                const raw = ai?.raw || {};
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Root Cause - Full Width */}
                    {raw.root_cause && (
                      <Box 
                        sx={{ 
                          backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                          borderRadius: 3,
                          p: 3,
                          borderLeft: '4px solid #ef4444'
                        }}
                      >
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 1.5, 
                            color: '#ef4444',
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em'
                          }}
                        >
                          🔍 Root Cause Analysis
                        </Typography>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.7,
                            fontSize: '0.95rem',
                            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)'
                          }}
                        >
                          {raw.root_cause}
                        </Typography>
                      </Box>
                    )}

                    {/* Resolution and Prevention - Grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                      {raw.resolution && (
                        <Box 
                          sx={{ 
                            backgroundColor: mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                            borderRadius: 3,
                            p: 2.5,
                            borderLeft: '4px solid #22c55e'
                          }}
                        >
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 1.5, 
                              color: '#22c55e',
                              fontSize: '1rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            ✅ Resolution
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{raw.resolution}</Typography>
                        </Box>
                      )}
                      {raw.prevention && (
                        <Box 
                          sx={{ 
                            backgroundColor: mode === 'dark' ? 'rgba(251, 146, 60, 0.1)' : 'rgba(251, 146, 60, 0.05)',
                            borderRadius: 3,
                            p: 2.5,
                            borderLeft: '4px solid #fb923c'
                          }}
                        >
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 1.5, 
                              color: '#fb923c',
                              fontSize: '1rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            🛡️ Prevention Strategies
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{raw.prevention}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Lessons Learned and Recommendations - Grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                      {raw.lessons_learned && (
                        <Box 
                          sx={{ 
                            backgroundColor: mode === 'dark' ? 'rgba(168, 85, 247, 0.1)' : 'rgba(168, 85, 247, 0.05)',
                            borderRadius: 3,
                            p: 2.5,
                            borderLeft: '4px solid #a855f7'
                          }}
                        >
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 1.5, 
                              color: '#a855f7',
                              fontSize: '1rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            💡 Lessons Learned
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{raw.lessons_learned}</Typography>
                        </Box>
                      )}
                      {raw.recommendations && (
                        <Box 
                          sx={{ 
                            backgroundColor: mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
                            borderRadius: 3,
                            p: 2.5,
                            borderLeft: '4px solid #3b82f6'
                          }}
                        >
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 700, 
                              mb: 1.5, 
                              color: '#3b82f6',
                              fontSize: '1rem',
                              letterSpacing: '-0.01em'
                            }}
                          >
                            📋 Recommendations
                          </Typography>
                          {Array.isArray(raw.recommendations) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {raw.recommendations.map((rec: string, idx: number) => (
                                <Typography key={idx} variant="body2" sx={{ lineHeight: 1.7 }}>• {rec}</Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{raw.recommendations}</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    {/* Technical Insights - Full Width */}
                    {raw.technical_insights && Array.isArray(raw.technical_insights) && raw.technical_insights.length > 0 && (
                      <Box 
                        sx={{ 
                          backgroundColor: mode === 'dark' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.05)',
                          borderRadius: 3,
                          p: 3,
                          borderLeft: '4px solid #0ea5e9'
                        }}
                      >
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 1.5, 
                            color: '#0ea5e9',
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em'
                          }}
                        >
                          ⚙️ Technical Insights
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {raw.technical_insights.map((insight: string, idx: number) => (
                            <Typography key={idx} variant="body2" sx={{ lineHeight: 1.7 }}>• {insight}</Typography>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Timeline - Full Width */}
                    {raw.timeline && Array.isArray(raw.timeline) && raw.timeline.length > 0 && (
                      <Box 
                        sx={{ 
                          backgroundColor: mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.05)',
                          borderRadius: 3,
                          p: 3,
                          borderLeft: '4px solid #94a3b8'
                        }}
                      >
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 700, 
                            mb: 1.5, 
                            color: '#94a3b8',
                            fontSize: '1.1rem',
                            letterSpacing: '-0.01em'
                          }}
                        >
                          ⏱️ Resolution Timeline
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {raw.timeline.map((t: any, idx: number) => (
                            <Typography 
                              key={idx} 
                              variant="body2" 
                              sx={{ 
                                lineHeight: 1.7,
                                fontWeight: 500
                              }}
                            >
                              {idx + 1}. {typeof t === 'string' ? t : (t?.text || '')}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()}
            </CardContent>
          </Card>
        )}

    </Box>
  );
}
